import { SearchParamController } from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import { calculatePercentageChange, formatFullCurrency } from '../../utils/numbers';
import { parsePeriodParams, serializePeriodParams } from '../../utils/periodParams';
import BillingDelegate from './billing.delegate';
import {
    BILLING_DEFAULT_TIMEFRAME,
    BILLING_DATE_RANGE_SEARCH_PARAM_KEY,
    BILLING_TIMEFRAME_SEARCH_PARAM_KEY,
} from './billing.constants';

class BillingController extends Inherit(ClockoutController, BillingDelegate, SearchParamController) {
    state = {
        user: {},
        loading: true,
        timeFrame: BILLING_DEFAULT_TIMEFRAME,
        kpiData: null,
        kpiLoaded: false,
        statements: [],
        statementsLoaded: false,
        dateRange: null,
        selectedStatements: new Set(),
    };

    async initialize() {
        if (!this.user?.access?.length) {
            this.navigateLogin();
            return;
        }

        const { timeFrame, dateRange } = this.resolveInitialBillingPeriod();

        this.setState({
            loading: true,
            user: this.data.user,
            timeFrame,
            dateRange,
        });

        await this.refreshBillingData({ timeFrame, dateRange });

        this.setState({ loading: false });
    }

    getFormattedInvoiceAmount() {
        return formatFullCurrency(this.state.kpiData?.invoiceAmount);
    }

    /**
     * Resolve Billing's period controls from the URL so refreshes/deep links
     * preserve the selected KPI/table date window.
     *
     * @returns {{ timeFrame: string, dateRange: import('dayjs').Dayjs[] | null }}
     */
    resolveInitialBillingPeriod() {
        const periodParam = this.getSearchParamValue(BILLING_TIMEFRAME_SEARCH_PARAM_KEY);
        const rangeParam = this.getSearchParamValue(BILLING_DATE_RANGE_SEARCH_PARAM_KEY);
        const resolved = parsePeriodParams(periodParam, rangeParam, BILLING_DEFAULT_TIMEFRAME);
        const { period, range } = serializePeriodParams(resolved.timeFrame, resolved.dateRange);

        // When `period=custom` lacks a valid range (or a stale `range` lingers
        // on a preset), rewrite the URL so it matches resolved state.
        const normalizedRange = range ? range.join(',') : null;
        if (periodParam !== period || (rangeParam ?? null) !== normalizedRange) {
            this.persistBillingPeriod(resolved.timeFrame, resolved.dateRange);
        }

        return resolved;
    }

    persistBillingPeriod(timeFrame, dateRange) {
        const { period, range } = serializePeriodParams(timeFrame, dateRange);
        this.setSearchParamValue(BILLING_TIMEFRAME_SEARCH_PARAM_KEY, period);
        // SearchParamController joins arrays with `,` → `range=YYYY-MM-DD,YYYY-MM-DD`.
        this.setSearchParamValue(BILLING_DATE_RANGE_SEARCH_PARAM_KEY, range);
    }

    mergeBillableFromAnalytics(baseKpi, billableUsersData, priorBillableUsersData) {
        const billableUsers = billableUsersData?.billable_users ?? 0;
        const dollarsPerBillableUser = 3;
        const priorBillableUsers = priorBillableUsersData?.billable_users;
        const billableUsersChange = Number.isFinite(priorBillableUsers)
            ? calculatePercentageChange(billableUsers, priorBillableUsers)
            : null;

        return {
            ...baseKpi,
            billableUsers,
            billableUsersChange,
            invoiceAmount: billableUsers * dollarsPerBillableUser,
            invoiceAmountChange: billableUsersChange,
        };
    }

    async exportToXLSX() {
        try {
            const { selectedStatements, statements } = this.state;
            const statementIds =
                selectedStatements.size > 0 ? Array.from(selectedStatements) : statements.map((s) => s.id);

            await this.data.exportBillingStatements(statementIds);
        } catch (error) {
            console.error('Error exporting statements:', error);
        }
    }
}

export default BillingController;
